import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FieldInputProps, useFormikContext } from 'formik';
import utils from 'utils';
import actions from 'state/actions';
import service from 'services';
import selectors from 'state/selectors';
import { actionType } from 'utils/constants';
import { AIsuggestion, FieldGroup } from 'models/response';
import { ActiveBox, Q1Value } from 'models/documents';
import { RootState } from 'models';
import userHelper from 'utils/helpers/user.helper';

interface UseInputQ1LogicProps {
  name: string;
  protocol: number | null;
  idField: number;
  readonly?: boolean;
  hasQ3?: boolean;
  fieldGroup?: FieldGroup | null;
  field: FieldInputProps<Q1Value | undefined>
  multi?: boolean;
  index?: number;
}

export const useInputQ1Logic = ({
  name,
  protocol,
  idField,
  readonly,
  hasQ3,
  fieldGroup,
  field,
  multi,
  index,
}: UseInputQ1LogicProps) => {
  const dispatch = useDispatch();
  const { status } = useFormikContext();
  const [suggestions, setSuggestion] = useState<AIsuggestion[] | null>(null);
  const [showSuggestionsBox, setShowSuggestionsBox] = useState(false);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const t = utils.intl.useTranslator();

  const disabledCheck = companyName
    ? !utils.user.isActionByCompanyActive(actionType.CAN_EDIT_Q3, companyName) || (readonly && !hasQ3)
    : readonly && !hasQ3;
  const isAutoVIewInPageActive = utils.user.getConfig(actionType.AUTO_VIEW_IN_PAGE);
  const isQ3visible = useSelector(selectors.documents.selectShowBody);
  const records = useSelector(selectors.documents.selectAllActiveDocuments);
  const config = useSelector((store: RootState) =>
    selectors.app.getConfig(store, actionType.CLEAR_ZERO_VALUE_Q1_CURRENCY_FIELD),
  );
  const decimalScale = useSelector(selectors.documents.selectDocumentDecimal);

  useEffect(() => {
    setCompanyName(records.length ? userHelper.getCompany(records,'companyName') : null);
  }, [records]);

  const getSuggestions = async () => {
    try {
      if (protocol) {
        const { data } = await service.getAIsuggestions(protocol, idField);
        return data;
      }
    } catch (err) {
      console.error(err);
    }
  };

  const showQ3 = () => {
    if (fieldGroup) {
      dispatch(actions.documents.setBodyColumns(fieldGroup.fieldColumn));
      dispatch(actions.documents.setBalanceField(fieldGroup.balanceField));
      dispatch(actions.documents.setHeaderNameQ3(fieldGroup.name));
      dispatch(actions.documents.setBodyName(name));
      dispatch(actions.documents.setShowBody(true));
      dispatch(actions.documents.setFieldGroupId(fieldGroup.idFieldGroup));
      dispatch(actions.documents.setPredictionsData(fieldGroup.predictionsValues));
    }
  };

  const onIconClick = async () => {
    if (readonly && !hasQ3) return;

    dispatch(actions.documents.setActiveBox(null));

    if (showSuggestionsBox) {
      return setShowSuggestionsBox(false);
    }

    dispatch(actions.documents.setAiSuggestions([]));
    const data = await getSuggestions();

    if (data && data.length) {
      setSuggestion(data);
      dispatch(actions.documents.setAiSuggestions(data));
    } else {
      !hasQ3 && utils.app.notify('warning', t('no suggestions'), 5000, 'no-suggestions');
    }

    if (hasQ3 && fieldGroup) {
      showQ3();
    } else {
      setShowSuggestionsBox(true);
    }
  };

  const autoViewInPage = () => {
    try {
      if (!isAutoVIewInPageActive) return;
      if (!field.value) {
        utils.app.notify('warning', t('value is missing'));
        return;
      }
      dispatch(actions.documents.setActiveBox(null));
      if ((field.value.content[0].pageNumber ?? -1) !== -1) {
        const activeBox = {
          ...field.value.content[index ?? 0].boundingBox,
          pageNumber: field.value.content[index ?? 0].pageNumber,
        };
        dispatch(actions.documents.setActiveBox(activeBox));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getDefaultSuggestion = () => {
    let defaultSuggestion = '';
    if (field.value && typeof field.value.content === 'object') {
      const {
        confidence,
        boundingBox: { x1, x2, y1, y2 },
        pageNumber,
      } = field.value.content[multi && (index || index === 0) ? index : 0];
      defaultSuggestion = `${confidence}-${pageNumber}-${x1},${y1}-${x2},${y2}`;
    }
    return defaultSuggestion;
  };

  const defaultSuggestion = useMemo(getDefaultSuggestion, [field.value, multi, index]);

  const setActiveBox = (value: ActiveBox | null) => {
    dispatch(actions.documents.setActiveBox(value));
  };

  return {
    suggestions,
    showSuggestionsBox,
    companyName,
    setCompanyName,
    isAutoVIewInPageActive,
    isQ3visible,
    config,
    decimalScale,
    status,
    defaultSuggestion,
    onIconClick,
    setShowSuggestionsBox,
    setSuggestion,
    autoViewInPage,
    setActiveBox,
    disabledCheck,
  };
};
